import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Skeleton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  LocationOn,
  Home,
  AttachMoney,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  CloudUpload as CloudUploadIcon,
  PhotoCamera,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { propertyApi, unitApi, ownerApi, unitTypeApi, UnitType, Unit } from '../services/api';
import { PropertyMediaUpload } from '../components/PropertyMediaUpload';
import { MapDisplay } from '../components/MapDisplay';
import { EmbeddedMapPicker } from '../components/EmbeddedMapPicker';
import HierarchicalUnitTree from '../components/HierarchicalUnitTree';
import EditUnitDialog from '../components/EditUnitDialog';
import { formatDate, safeString, safeArray, debugLogPropertyData, formatAddress } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { countries, getPhoneCodeByCountryName, Country } from '../utils/countries';
import { getStorageUrl, getApiUrl } from '../config/api';

interface UnitFormData {
  parent_unit_id?: number;
  unit_number: string;
  type: string; // Dynamic unit type
  name: string;
  rent_amount: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  status: 'vacant' | 'occupied' | 'maintenance';
  description?: string;
  rental_types: ('Hour' | '3 Hours' | 'Daily' | 'Monthly' | 'Yearly')[];
  sort_order?: number;
}

const PropertyDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated, isAdmin } = useAuth();
  const { t } = useTranslation();
  const [openUnitDialog, setOpenUnitDialog] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
  const [parentUnit, setParentUnit] = useState<Unit | null>(null);
  const [possibleParents, setPossibleParents] = useState<Unit[]>([]);
  const [editUnitDialogOpen, setEditUnitDialogOpen] = useState(false);

  // Editing state variables
  const [editingProperty, setEditingProperty] = useState(false);
  const [editingFacilities, setEditingFacilities] = useState(false);
  const [editingSpecialSpecs, setEditingSpecialSpecs] = useState(false);
  const [editingOwner, setEditingOwner] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [newFacility, setNewFacility] = useState('');
  const [newSpecialSpec, setNewSpecialSpec] = useState('');
  const [propertyFormData, setPropertyFormData] = useState<any>({});
  const [selectedLocation, setSelectedLocation] = useState<{lat: number, lng: number} | null>(null);
  const [locationUrl, setLocationUrl] = useState<string>('');
  const [urlError, setUrlError] = useState<string>('');
  const [ownerFormData, setOwnerFormData] = useState({
    name: '',
    nationality: '',
    mobile_number: '',
    address: '',
  });

  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<UnitFormData>({
    defaultValues: {
      parent_unit_id: undefined,
      unit_number: '',
      type: 'apartment',
      name: '',
      rent_amount: 0,
      bedrooms: 1,
      bathrooms: 1,
      square_feet: 0,
      status: 'vacant',
      description: '',
      rental_types: [],
      sort_order: 0,
    },
  });

  // Fetch property details
  const { data: property, isLoading: propertyLoading, error: propertyError } = useQuery({
    queryKey: ['property', id],
    queryFn: async () => {
      const result = await propertyApi.getById(Number(id));
      debugLogPropertyData(result, 'PropertyDetails fetch');
      return result;
    },
    enabled: !!id,
  });

  // Fetch units for this property (hierarchical tree)
  const { data: units = [], isLoading: unitsLoading, refetch: refetchUnits } = useQuery({
    queryKey: ['units-tree', id],
    queryFn: () => unitApi.getTree(Number(id)),
    enabled: !!id,
  });

  // Fetch currencies for currency selection
  const { data: currencies = [], isLoading: isLoadingCurrencies, error: currenciesError } = useQuery({
    queryKey: ['currencies'],
    queryFn: async () => {
      const response = await fetch(getApiUrl('/currencies'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch currencies');
      }
      
      const data = await response.json();
      // Ensure we always return an array
      const currenciesArray = Array.isArray(data) ? data : (data.data || []);
      console.log('PropertyDetails - Fetched currencies:', currenciesArray); // Debug log
      return currenciesArray;
    },
    retry: 3,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch unit types for unit type selection
  const { data: unitTypes = [], isLoading: unitTypesLoading } = useQuery({
    queryKey: ['unitTypes'],
    queryFn: async () => {
      return await unitTypeApi.getActive();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create unit mutation
  const createUnitMutation = useMutation({
    mutationFn: unitApi.create,
    onSuccess: (data) => {
      console.log('Unit created successfully:', data);
      refetchUnits();
      setOpenUnitDialog(false);
      reset();
    },
    onError: (error) => {
      console.error('Failed to create unit:', error);
    },
  });

  // Update unit mutation
  const updateUnitMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Unit> }) => unitApi.update(id, data),
    onSuccess: (data) => {
      console.log('Unit updated successfully:', data);
      refetchUnits();
      setOpenUnitDialog(false);
      setEditingUnit(null);
      reset();
    },
    onError: (error: any) => {
      console.error('Failed to update unit:', error);
      
      let errorMessage = 'Failed to update unit. Please try again.';
      
      // Handle validation errors
      if (error.response?.status === 422) {
        if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data?.errors) {
          const errors = Object.values(error.response.data.errors).flat();
          errorMessage = errors.join('\n');
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } 
      // Handle authorization errors
      else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to update this unit.';
      }
      // Handle not found errors
      else if (error.response?.status === 404) {
        errorMessage = 'Unit not found. It may have been deleted.';
      }
      // Handle network errors
      else if (!error.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      alert(errorMessage);
    },
  });

  // Delete unit mutation
  const deleteUnitMutation = useMutation({
    mutationFn: unitApi.delete,
    onSuccess: () => {
      console.log('Unit deleted successfully');
      refetchUnits();
    },
    onError: (error: any) => {
      console.error('Failed to delete unit:', error);
      
      let errorMessage = 'Failed to delete unit. ';
      
      // Handle specific error types from backend
      if (error.response?.status === 422) {
        if (error.response.data?.error) {
          const errorType = error.response.data.error;
          const message = error.response.data.message;
          
          switch (errorType) {
            case 'has_child_units':
              errorMessage = `Cannot delete unit. It has ${error.response.data.child_unit_count} sub-unit(s) associated with it. Please delete the sub-units first.`;
              break;
            case 'has_bookings':
              errorMessage = `Cannot delete unit. It has ${error.response.data.booking_count} booking(s) associated with it. Please delete or reassign the bookings first.`;
              break;
            case 'has_accounting_transactions':
              errorMessage = `Cannot delete unit. It has ${error.response.data.transaction_count} accounting transaction(s) associated with it. Please delete the transactions first.`;
              break;
            case 'has_purchase_invoices':
              errorMessage = `Cannot delete unit. It has ${error.response.data.invoice_count} purchase invoice(s) associated with it. Please delete the invoices first.`;
              break;
            default:
              errorMessage = message || 'Cannot delete unit due to existing dependencies.';
          }
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = 'Cannot delete unit due to existing dependencies.';
        }
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to delete this unit.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Unit not found. It may have been deleted already.';
      } else if (!error.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        errorMessage += 'Please try again.';
      }
      
      alert(errorMessage);
    },
  });

  // Update property mutation
  const updatePropertyMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => propertyApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property', id] });
      setEditingProperty(false);
      setEditingFacilities(false);
      setEditingSpecialSpecs(false);
      setEditingOwner(false);
      setEditingLocation(false);
    },
    onError: (error: any) => {
      console.error('Failed to update property:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      let errorMessage = 'Failed to update property. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMessage += '\n' + errors.join('\n');
      }
      
      alert(errorMessage);
    },
  });

  // Upload logo mutation
  const uploadLogoMutation = useMutation({
    mutationFn: async ({ propertyId, logoFile }: { propertyId: number; logoFile: File }) => {
      // Use the dedicated logo upload endpoint
      return await propertyApi.uploadLogo(propertyId, logoFile);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property', id] });
      setUploadingLogo(false);
      setLogoFile(null);
      setLogoPreview(null);
      alert('Logo uploaded successfully!');
    },
    onError: (error: any) => {
      console.error('Failed to upload logo:', error);
      setUploadingLogo(false);
      
      let errorMessage = 'Failed to upload logo. ';
      if (error?.response?.status === 404) {
        errorMessage += 'The upload endpoint is not available on the server. Please contact your administrator.';
      } else if (error?.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage += error.response.data.error;
      } else {
        errorMessage += 'Please try again.';
      }
      
      alert(errorMessage);
    },
  });

  // Get default coordinates for a city
  const getDefaultCoordinates = async (city: string, country: string) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(city + ', ' + country)}&limit=1`
      );
      const data = await response.json();
      if (data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
      }
    } catch (error) {
      console.error('Error getting default coordinates:', error);
    }
    // Fallback to world center
    return { lat: 0, lng: 0 };
  };

  // Function to map old rental types to new ones
  const mapRentalTypes = (types: string[]): ('Hour' | '3 Hours' | 'Daily' | 'Monthly' | 'Yearly')[] => {
    const typeMapping: { [key: string]: 'Hour' | '3 Hours' | 'Daily' | 'Monthly' | 'Yearly' } = {
      'Hour': 'Hour',
      '3 Hours': '3 Hours',
      'Daily': 'Daily',
      'Monthly': 'Monthly',
      'Yearly': 'Yearly',
      'Daily/Monthly': 'Daily', // Map old combined type to Daily
      'Monthly/Yearly': 'Monthly', // Map old combined type to Monthly
    };
    
    return types
      .map(type => typeMapping[type])
      .filter((type): type is 'Hour' | '3 Hours' | 'Daily' | 'Monthly' | 'Yearly' => type !== undefined);
  };

  const handleOpenUnitDialog = async (unit?: Unit, parent?: Unit) => {
    setParentUnit(parent || null);
    
    // Load possible parents for this unit
    try {
      const parents = await unitApi.getPossibleParents(Number(id), unit?.id);
      setPossibleParents(parents);
    } catch (error) {
      console.error('Failed to load possible parents:', error);
      setPossibleParents([]);
    }

    if (unit) {
      // For editing, use the EditUnitDialog
      setEditingUnit(unit);
      setEditUnitDialogOpen(true);
    } else {
      // For creating new units, use the original dialog
      setEditingUnit(null);
      reset({
        parent_unit_id: parent?.id,
        unit_number: '',
        type: unitTypes.length > 0 ? unitTypes[0].name : 'apartment',
        name: '',
        rent_amount: 0,
        bedrooms: 1,
        bathrooms: 1,
        square_feet: 0,
        status: 'vacant',
        description: '',
        rental_types: [],
        sort_order: 0,
      });
      setOpenUnitDialog(true);
    }
  };

  const handleCloseUnitDialog = () => {
    setOpenUnitDialog(false);
    setEditingUnit(null);
    setParentUnit(null);
    setPossibleParents([]);
    reset();
  };

  const onSubmitUnit = (data: UnitFormData) => {
    console.log('Submitting unit data:', data);
    const unitData = {
      ...data,
      property_id: Number(id),
      sort_order: data.sort_order || 0,
    };

    if (editingUnit) {
      console.log('Updating unit:', editingUnit.id);
      updateUnitMutation.mutate({ id: editingUnit.id, data: unitData });
    } else {
      console.log('Creating new unit');
      createUnitMutation.mutate(unitData);
    }
  };

  const handleEditUnitSubmit = (data: any) => {
    console.log('Submitting edit unit data:', data);
    const unitData = {
      ...data,
      property_id: Number(id),
    };

    if (editingUnit) {
      console.log('Updating unit:', editingUnit.id);
      updateUnitMutation.mutate({ id: editingUnit.id, data: unitData });
    }
  };

  const handleDeleteUnit = (unitId: number) => {
    console.log('Attempting to delete unit:', unitId);
    if (window.confirm('Are you sure you want to delete this unit?')) {
      deleteUnitMutation.mutate(unitId);
    }
  };

  const handleViewUnit = (unit: Unit) => {
    console.log('Viewing unit:', unit.id);
    navigate(`/units/${unit.id}`);
  };

  // Property editing handlers
  const handleEditProperty = () => {
    setPropertyFormData({
      name: property?.name || '',
      type: property?.type || 'apartment',
      address: property?.address || '',
      city: property?.city || '',
      country: property?.country || '',
      description: property?.description || '',
      mobile_phone_1: property?.mobile_phone_1 || '',
      mobile_phone_2: property?.mobile_phone_2 || '',
      currency_id: property?.currency_id || undefined,
    });
    setEditingProperty(true);
  };

  const handleSaveProperty = () => {
    // Basic validation
    if (!propertyFormData.name?.trim()) {
      alert('Property name is required');
      return;
    }
    if (!propertyFormData.address?.trim()) {
      alert('Property address is required');
      return;
    }
    if (!propertyFormData.city?.trim()) {
      alert('City is required');
      return;
    }
    if (!propertyFormData.country?.trim()) {
      alert('Country is required');
      return;
    }

    updatePropertyMutation.mutate({
      id: Number(id),
      data: propertyFormData
    });
  };

  const handleCancelPropertyEdit = () => {
    setEditingProperty(false);
    setPropertyFormData({});
  };

  // Facilities handlers
  const handleAddFacility = () => {
    if (newFacility.trim() && property) {
      const updatedFacilities = [...(property.facilities || []), newFacility.trim()];
      console.log('Updating facilities:', { facilities: updatedFacilities });
      updatePropertyMutation.mutate({
        id: Number(id),
        data: { facilities: updatedFacilities }
      });
      setNewFacility('');
    }
  };

  const handleRemoveFacility = (facilityIndex: number) => {
    if (property) {
      const updatedFacilities = property.facilities?.filter((_, index) => index !== facilityIndex) || [];
      console.log('Removing facility, updated facilities:', { facilities: updatedFacilities });
      updatePropertyMutation.mutate({
        id: Number(id),
        data: { facilities: updatedFacilities }
      });
    }
  };

  // Special specs handlers
  const handleAddSpecialSpec = () => {
    if (newSpecialSpec.trim() && property) {
      const updatedSpecs = [...(property.special_specs || []), newSpecialSpec.trim()];
      console.log('Updating special specs:', { special_specs: updatedSpecs });
      updatePropertyMutation.mutate({
        id: Number(id),
        data: { special_specs: updatedSpecs }
      });
      setNewSpecialSpec('');
    }
  };

  const handleRemoveSpecialSpec = (specIndex: number) => {
    if (property) {
      const updatedSpecs = property.special_specs?.filter((_, index) => index !== specIndex) || [];
      console.log('Removing special spec, updated specs:', { special_specs: updatedSpecs });
      updatePropertyMutation.mutate({
        id: Number(id),
        data: { special_specs: updatedSpecs }
      });
    }
  };

  // Owner editing handlers
  const handleEditOwner = () => {
    if (property?.owner) {
      setOwnerFormData({
        name: property.owner.name || '',
        nationality: property.owner.nationality || '',
        mobile_number: property.owner.mobile_number || '',
        address: property.owner.address || '',
      });
    } else {
      setOwnerFormData({
        name: '',
        nationality: '',
        mobile_number: '',
        address: '',
      });
    }
    setEditingOwner(true);
  };

  const handleSaveOwner = async () => {
    if (!ownerFormData.name.trim()) {
      alert('Owner name is required');
      return;
    }

    // Debug: Check authentication
    const token = localStorage.getItem('token');
    console.log('Authentication status:', {
      isAuthenticated,
      hasToken: !!token,
      user: user?.name || 'Not logged in',
      userId: user?.id || 'N/A'
    });
    console.log('Owner form data:', ownerFormData);

    try {
      if (property?.owner?.id) {
        // Update existing owner
        console.log('Updating existing owner with ID:', property.owner.id);
        await ownerApi.update(property.owner.id, ownerFormData);
        // Refresh property data to get updated owner info
        queryClient.invalidateQueries({ queryKey: ['property', id] });
        setEditingOwner(false);
        alert('Owner information updated successfully');
      } else {
        // Create new owner and assign to property
        console.log('Creating new owner for property ID:', id);
        const newOwner = await ownerApi.create(ownerFormData);
        console.log('New owner created:', newOwner);
        
        // Now update the property with the new owner_id
        try {
          console.log('Assigning owner to property using dedicated endpoint...');
          console.log('Owner ID:', newOwner.id, 'Property ID:', id);
          
          await propertyApi.assignOwner(Number(id), newOwner.id);
          // Refresh property data to get updated owner info
          queryClient.invalidateQueries({ queryKey: ['property', id] });
          setEditingOwner(false);
          alert('Owner created and assigned to property successfully');
        } catch (propertyError: any) {
          console.error('Error assigning owner to property:', propertyError);
          console.log('Property assignment error details:', propertyError?.response);
          
          // Show specific error message
          const errorMessage = propertyError?.response?.data?.message || 
                              propertyError?.message || 
                              'Unknown error occurred';
          alert(`Owner was created but failed to assign to property: ${errorMessage}`);
        }
      }
    } catch (error: any) {
      console.error('Error saving owner:', error);
      console.log('Full error response:', error?.response);
      
      // Check if it's an authentication error
      if (error?.response?.status === 401) {
        alert('Session expired. Please log in again.');
        return;
      }
      
      // Check if it's a validation error
      if (error?.response?.status === 422) {
        const validationErrors = error?.response?.data?.errors;
        if (validationErrors) {
          const errorMessages = Object.values(validationErrors).flat().join('\n');
          alert(`Validation errors:\n${errorMessages}`);
        } else {
          alert('Invalid data provided. Please check your input.');
        }
        return;
      }
      
      // Generic error message
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`Failed to save owner information: ${errorMessage}`);
    }
  };

  const handleCancelOwnerEdit = () => {
    setEditingOwner(false);
  };

  // Location editing handlers
  const handleEditLocation = async () => {
    setEditingLocation(true);
    
    // If property already has coordinates, use them
    if (property?.latitude && property?.longitude) {
      setSelectedLocation({ 
        lat: Number(property.latitude), 
        lng: Number(property.longitude) 
      });
    } else {
      // Get default coordinates for the property's city
      if (property?.city && property?.country) {
        const defaultCoords = await getDefaultCoordinates(property.city, property.country);
        setSelectedLocation(defaultCoords);
      } else {
        // Fallback to world center
        setSelectedLocation({ lat: 0, lng: 0 });
      }
    }
  };

  const handleLocationSelected = useCallback((lat: number, lng: number) => {
    setSelectedLocation({ lat: Number(lat), lng: Number(lng) });
  }, []);

  const handleSaveLocation = () => {
    if (!selectedLocation) {
      alert(t('please_select_location'));
      return;
    }

    updatePropertyMutation.mutate({
      id: Number(id),
      data: {
        latitude: selectedLocation.lat,
        longitude: selectedLocation.lng
      }
    });
  };

  const handleCancelLocationEdit = () => {
    setEditingLocation(false);
    setSelectedLocation(null);
    setLocationUrl('');
    setUrlError('');
  };

  // Function to extract coordinates from various map URLs
  const extractCoordinatesFromUrl = (url: string): { lat: number, lng: number } | null => {
    try {
      // Google Maps patterns
      const googleMapsPatterns = [
        /(?:google\.com\/maps.*[@?])([-\d.]+),([-\d.]+)/i,
        /(?:maps\.google\.com.*[@?])([-\d.]+),([-\d.]+)/i,
        /(?:goo\.gl\/maps.*)([-\d.]+),([-\d.]+)/i,
        /(?:google\.com\/maps.*q=)([-\d.]+),([-\d.]+)/i,
        /(?:google\.com\/maps.*ll=)([-\d.]+),([-\d.]+)/i,
      ];

      // OpenStreetMap patterns
      const osmPatterns = [
        /(?:openstreetmap\.org.*[?&]mlat=)([-\d.]+).*[?&]mlon=([-\d.]+)/i,
        /(?:openstreetmap\.org.*#map=\d+\/)([-\d.]+)\/([-\d.]+)/i,
        /(?:osm\.org.*[?&]mlat=)([-\d.]+).*[?&]mlon=([-\d.]+)/i,
      ];

      // Apple Maps patterns
      const appleMapsPatterns = [
        /(?:maps\.apple\.com.*[@?])([-\d.]+),([-\d.]+)/i,
      ];

      // Bing Maps patterns
      const bingMapsPatterns = [
        /(?:bing\.com\/maps.*cp=)([-\d.]+)~([-\d.]+)/i,
      ];

      // Generic coordinate patterns (lat,lng or lng,lat)
      const genericPatterns = [
        /([-\d.]+),\s*([-\d.]+)/,
      ];

      const allPatterns = [
        ...googleMapsPatterns,
        ...osmPatterns,
        ...appleMapsPatterns,
        ...bingMapsPatterns,
        ...genericPatterns,
      ];

      for (const pattern of allPatterns) {
        const match = url.match(pattern);
        if (match) {
          const lat = parseFloat(match[1]);
          const lng = parseFloat(match[2]);
          
          // Validate coordinates
          if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            return { lat, lng };
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error extracting coordinates from URL:', error);
      return null;
    }
  };

  const handleUrlInput = () => {
    setUrlError('');
    
    if (!locationUrl.trim()) {
      setUrlError(t('please_enter_location_url'));
      return;
    }

    const coordinates = extractCoordinatesFromUrl(locationUrl);
    
    if (coordinates) {
      setSelectedLocation({ lat: coordinates.lat, lng: coordinates.lng });
      setUrlError('');
    } else {
      setUrlError(t('could_not_extract_coordinates'));
    }
  };

  // Logo upload handlers
  const handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = () => {
    if (logoFile && id) {
      setUploadingLogo(true);
      uploadLogoMutation.mutate({
        propertyId: Number(id),
        logoFile: logoFile,
      });
    }
  };

  const handleCancelLogoUpload = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  if (propertyLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Container>
    );
  }

  if (propertyError || !property) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load property details. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/properties')}
          variant="outlined"
        >
          Back to Properties
        </Button>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {property.name}
        </Typography>
        {isAdmin && (
          <Tooltip title="Edit Property Information">
            <Button
              startIcon={<EditIcon />}
              onClick={handleEditProperty}
              variant="outlined"
              color="primary"
            >
              Edit Property
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Property Details Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Property Information
                </Typography>
                {editingProperty && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<SaveIcon />}
                      variant="contained"
                      color="primary"
                      onClick={handleSaveProperty}
                      disabled={updatePropertyMutation.isPending}
                    >
                      Save
                    </Button>
                    <Button
                      startIcon={<CancelIcon />}
                      variant="outlined"
                      onClick={handleCancelPropertyEdit}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
              
              {editingProperty ? (
                /* Edit Mode Form */
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Property Name"
                    fullWidth
                    required
                    value={propertyFormData.name || ''}
                    onChange={(e) => setPropertyFormData({ ...propertyFormData, name: e.target.value })}
                    error={!propertyFormData.name?.trim()}
                    helperText={!propertyFormData.name?.trim() ? 'Property name is required' : ''}
                  />
                  
                  <FormControl fullWidth required>
                    <InputLabel>{t('property_type')}</InputLabel>
                    <Select
                      value={propertyFormData.type || 'apartment'}
                      label={t('property_type')}
                      onChange={(e) => setPropertyFormData({ ...propertyFormData, type: e.target.value })}
                    >
                      <MenuItem value="apartment">{t('property_type_apartment')}</MenuItem>
                      <MenuItem value="house">{t('property_type_house')}</MenuItem>
                      <MenuItem value="condo">{t('property_type_condo')}</MenuItem>
                      <MenuItem value="townhouse">{t('property_type_townhouse')}</MenuItem>
                      <MenuItem value="commercial">{t('property_type_commercial')}</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Address"
                    fullWidth
                    required
                    multiline
                    rows={2}
                    value={propertyFormData.address || ''}
                    onChange={(e) => setPropertyFormData({ ...propertyFormData, address: e.target.value })}
                    error={!propertyFormData.address?.trim()}
                    helperText={!propertyFormData.address?.trim() ? 'Address is required' : ''}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label="City"
                        fullWidth
                        required
                        value={propertyFormData.city || ''}
                        onChange={(e) => setPropertyFormData({ ...propertyFormData, city: e.target.value })}
                        error={!propertyFormData.city?.trim()}
                        helperText={!propertyFormData.city?.trim() ? 'City is required' : ''}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth required error={!propertyFormData.country?.trim()}>
                        <InputLabel>Country</InputLabel>
                        <Select
                          value={propertyFormData.country || ''}
                          label="Country"
                          onChange={(e) => setPropertyFormData({ ...propertyFormData, country: e.target.value })}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 200,
                              },
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select a country</em>
                          </MenuItem>
                          {countries.map((country: Country) => (
                            <MenuItem key={country.code} value={country.name}>
                              {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {!propertyFormData.country?.trim() && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
                            Country is required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>

                  <FormControl fullWidth>
                    <InputLabel>{t('currency')}</InputLabel>
                    <Select
                      value={propertyFormData.currency_id?.toString() || ''}
                      label={t('currency')}
                      onChange={(e) => setPropertyFormData({ 
                        ...propertyFormData, 
                        currency_id: e.target.value ? parseInt(e.target.value as string) : undefined 
                      })}
                    >
                      <MenuItem value="">
                        <em>{t('select_currency')}</em>
                      </MenuItem>
                      {Array.isArray(currencies) ? currencies
                        .filter((currency: any) => currency.is_active)
                        .map((currency: any) => (
                        <MenuItem key={currency.id} value={currency.id}>
                          {currency.code} - {currency.name} ({currency.symbol})
                        </MenuItem>
                      )) : null}
                    </Select>
                    {currenciesError && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
                        {t('failed_load_currencies')}
                      </Typography>
                    )}
                  </FormControl>

                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                    value={propertyFormData.description || ''}
                    onChange={(e) => setPropertyFormData({ ...propertyFormData, description: e.target.value })}
                    helperText="Optional description of the property"
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label={t('mobile_phone_1')}
                        fullWidth
                        value={propertyFormData.mobile_phone_1 || ''}
                        onChange={(e) => setPropertyFormData({ ...propertyFormData, mobile_phone_1: e.target.value })}
                        helperText="Optional contact phone number"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box sx={{ 
                                fontWeight: 'bold', 
                                color: 'primary.main',
                                bgcolor: 'grey.100',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.875rem'
                              }}>
                                {getPhoneCodeByCountryName(propertyFormData.country || '') || '+XX'}
                              </Box>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label={t('mobile_phone_2')}
                        fullWidth
                        value={propertyFormData.mobile_phone_2 || ''}
                        onChange={(e) => setPropertyFormData({ ...propertyFormData, mobile_phone_2: e.target.value })}
                        helperText="Optional secondary phone number"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box sx={{ 
                                fontWeight: 'bold', 
                                color: 'primary.main',
                                bgcolor: 'grey.100',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.875rem'
                              }}>
                                {getPhoneCodeByCountryName(propertyFormData.country || '') || '+XX'}
                              </Box>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                /* Display Mode */
                <>
                  {/* Basic Information */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Basic Details
                    </Typography>
                    {property.creator && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography>
                          Created by {property.creator.name}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography>
                        {formatAddress(
                          safeString(property.address, 'Address not available'),
                          property.city,
                          property.country
                        )}
                      </Typography>
                    </Box>
                    {property.description && (
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {property.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip
                        label={property.type}
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={`${units.length} Units`}
                        icon={<Home />}
                        variant="outlined"
                      />
                    </Box>
                  </Box>

                  {/* Contact Information */}
                  {(safeString(property.mobile_phone_1, '') !== '' || safeString(property.mobile_phone_2, '') !== '') && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Contact Information
                      </Typography>
                      {safeString(property.mobile_phone_1, '') !== '' && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>{t('mobile_phone_1')}:</strong> {getPhoneCodeByCountryName(property.country || '')} {property.mobile_phone_1}
                        </Typography>
                      )}
                      {safeString(property.mobile_phone_2, '') !== '' && (
                        <Typography variant="body2">
                          <strong>{t('mobile_phone_2')}:</strong> {getPhoneCodeByCountryName(property.country || '')} {property.mobile_phone_2}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Currency Information */}
                  {property.currency_id && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Currency
                      </Typography>
                      {(() => {
                        const currency = Array.isArray(currencies) 
                          ? currencies.find((c: any) => c.id === property.currency_id)
                          : null;
                        return currency ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AttachMoney sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              <strong>Currency:</strong> {currency.code} - {currency.name} ({currency.symbol})
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Currency information not available
                          </Typography>
                        );
                      })()}
                    </Box>
                  )}

                  {/* Property Logo Section */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" color="primary">
                        Property Logo
                      </Typography>
                      {isAdmin && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="logo-upload-button"
                            type="file"
                            onChange={handleLogoFileChange}
                          />
                          <label htmlFor="logo-upload-button">
                            <Button
                              variant="outlined"
                              component="span"
                              startIcon={<PhotoCamera />}
                              size="small"
                              disabled={uploadingLogo}
                            >
                              Select Logo
                            </Button>
                          </label>
                          {logoFile && (
                            <>
                              <Button
                                variant="contained"
                                onClick={handleUploadLogo}
                                disabled={uploadingLogo}
                                startIcon={<CloudUploadIcon />}
                                size="small"
                              >
                                {uploadingLogo ? 'Uploading...' : 'Upload'}
                              </Button>
                              <Button
                                variant="text"
                                onClick={handleCancelLogoUpload}
                                disabled={uploadingLogo}
                                size="small"
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                    
                    {/* Current Logo Display */}
                    {property.logo_url ? (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          p: 2,
                          border: '1px solid',
                          borderColor: 'grey.300',
                          borderRadius: 1,
                          bgcolor: 'grey.50'
                        }}>
                          <img
                            src={getStorageUrl(property.logo_url)}
                            alt="Property Logo"
                            style={{
                              maxWidth: '150px',
                              maxHeight: '100px',
                              objectFit: 'contain',
                              borderRadius: '4px'
                            }}
                            onError={(e) => {
                              console.error('Failed to load logo:', property.logo_url ? getStorageUrl(property.logo_url) : 'undefined path');
                              console.error('Storage URL config:', {
                                storage_url: process.env.REACT_APP_STORAGE_URL,
                                api_base_url: process.env.REACT_APP_API_BASE_URL
                              });
                              // Show error message instead of hiding
                              e.currentTarget.style.display = 'none';
                              const errorDiv = document.createElement('div');
                              errorDiv.style.color = 'red';
                              errorDiv.style.fontSize = '12px';
                              errorDiv.style.padding = '8px';
                              errorDiv.textContent = `Failed to load: ${property.logo_url ? getStorageUrl(property.logo_url) : 'undefined'}`;
                              e.currentTarget.parentNode?.appendChild(errorDiv);
                            }}
                            onLoad={() => {
                              console.log('Logo loaded successfully:', property.logo_url ? getStorageUrl(property.logo_url) : 'undefined path');
                            }}
                          />
                          <Box sx={{ ml: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Current property logo
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        !logoPreview && (
                          <Box sx={{ 
                            p: 2,
                            border: '1px dashed',
                            borderColor: 'grey.400',
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                            textAlign: 'center'
                          }}>
                            <PhotoCamera sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              No logo uploaded yet
                            </Typography>
                            {isAdmin && (
                              <Typography variant="caption" color="text.secondary">
                                Use the "Select Logo" button above to upload a logo
                              </Typography>
                            )}
                          </Box>
                        )
                      )}

                    {/* Logo Preview */}
                    {logoPreview && (
                      <Box sx={{ 
                        mt: 2,
                        p: 2,
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1,
                        bgcolor: 'primary.50'
                      }}>
                        <Typography variant="body2" color="primary" gutterBottom>
                          Preview:
                        </Typography>
                        <img
                          src={logoPreview}
                          alt="Logo Preview"
                          style={{
                            maxWidth: '150px',
                            maxHeight: '100px',
                            objectFit: 'contain',
                            borderRadius: '4px'
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          File: {logoFile?.name} ({((logoFile?.size || 0) / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Main Image Display Section */}
                  <Box>
                    <Typography variant="h6" gutterBottom color="primary">
                      Property Main Image
                    </Typography>
                    
                    {/* Current Main Image Display */}
                    {property.main_image ? (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          p: 2,
                          border: '1px solid',
                          borderColor: 'grey.300',
                          borderRadius: 1,
                          bgcolor: 'grey.50'
                        }}>
                          <img
                            src={getStorageUrl(property.main_image)}
                            alt="Property Main Image"
                            style={{
                              maxWidth: '200px',
                              maxHeight: '150px',
                              objectFit: 'contain',
                              borderRadius: '4px'
                            }}
                            onError={(e) => {
                              console.error('Failed to load main image:', property.main_image ? getStorageUrl(property.main_image) : 'undefined path');
                              // Show error message instead of hiding
                              e.currentTarget.style.display = 'none';
                              const errorDiv = document.createElement('div');
                              errorDiv.style.color = 'red';
                              errorDiv.style.fontSize = '12px';
                              errorDiv.style.padding = '8px';
                              errorDiv.textContent = `Failed to load: ${property.main_image ? getStorageUrl(property.main_image) : 'undefined'}`;
                              e.currentTarget.parentNode?.appendChild(errorDiv);
                            }}
                            onLoad={() => {
                              console.log('Main image loaded successfully:', property.main_image ? getStorageUrl(property.main_image) : 'undefined path');
                            }}
                          />
                          <Box sx={{ ml: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Current property main image
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ 
                          p: 2,
                          border: '1px dashed',
                          borderColor: 'grey.400',
                          borderRadius: 1,
                          bgcolor: 'grey.50',
                          textAlign: 'center'
                        }}>
                          <Typography variant="body2" color="text.secondary">
                            No main image uploaded yet
                          </Typography>
                        </Box>
                      )}
                  </Box>

                  {/* Creation Date */}
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Created on: {formatDate(property.created_at, 'long')}
                    </Typography>
                  </Box>
                </>
              )}
            </Grid>
            
            {/* Map Display with Picker */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      {t('location')}
                    </Typography>
                    {isAdmin && !editingLocation && (
                      <Button
                        startIcon={<LocationOn />}
                        variant="outlined"
                        size="small"
                        onClick={handleEditLocation}
                      >
                        {property?.latitude && property?.longitude ? t('edit_location') : t('set_location')}
                      </Button>
                    )}
                    {editingLocation && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          startIcon={<SaveIcon />}
                          variant="contained"
                          size="small"
                          onClick={handleSaveLocation}
                          disabled={!selectedLocation}
                        >
                          {t('save')}
                        </Button>
                        <Button
                          startIcon={<CancelIcon />}
                          variant="outlined"
                          size="small"
                          onClick={handleCancelLocationEdit}
                        >
                          {t('cancel')}
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {editingLocation ? (
                    <Box>
                      <EmbeddedMapPicker
                        onLocationSelect={handleLocationSelected}
                        initialLocation={selectedLocation}
                        height={400}
                      />
                      
                      {/* URL Input Section - More Prominent */}
                      <Box sx={{ mt: 3, p: 2, border: '1px dashed #ccc', borderRadius: 1, bgcolor: 'background.paper' }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: '8px' }}>🔗</span>
                          {t('or_enter_location_url')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                          {t('paste_url_from_google_maps_etc')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder={t('paste_location_url_placeholder')}
                            value={locationUrl}
                            onChange={(e) => setLocationUrl(e.target.value)}
                            error={!!urlError}
                            helperText={urlError}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleUrlInput}
                            disabled={!locationUrl.trim()}
                            sx={{ minWidth: '120px', height: '40px' }}
                          >
                            {t('extract')}
                          </Button>
                        </Box>
                      </Box>

                      {selectedLocation && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {t('selected_coordinates')}: {Number(selectedLocation.lat).toFixed(6)}, {Number(selectedLocation.lng).toFixed(6)}
                          </Typography>
                          <Typography variant="caption" color="primary">
                            <a 
                              href={`https://www.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textDecoration: 'none', color: 'inherit', marginRight: '12px' }}
                            >
                              🌐 {t('view_on_google_maps')}
                            </a>
                            <a 
                              href={`https://www.openstreetmap.org/?mlat=${selectedLocation.lat}&mlon=${selectedLocation.lng}&zoom=15`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                              🗺️ {t('view_on_openstreetmap')}
                            </a>
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      {property && property.latitude && property.longitude ? (
                        <Box sx={{ maxWidth: '60%' }}>
                        <MapDisplay
                          latitude={property.latitude}
                          longitude={property.longitude}
                          propertyName={property.name}
                          address={formatAddress(
                            property.address,
                            property.city,
                            property.country
                          )}
                          height={350}
                        />
                        </Box>
                      ) : (
                        <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
                          <LocationOn sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                          <Typography variant="body1" color="text.secondary" gutterBottom>
                            {t('no_location_set')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('click_set_location_to_add')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Owner Information
                </Typography>
                {isAdmin && (
                  <Button
                    startIcon={<PersonIcon />}
                    variant="outlined"
                    size="small"
                    onClick={handleEditOwner}
                  >
                    {editingOwner ? 'Cancel' : 'Edit Owner'}
                  </Button>
                )}
              </Box>

              {editingOwner ? (
                /* Owner Edit Form */
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <TextField
                    label="Owner Name"
                    fullWidth
                    required
                    value={ownerFormData.name}
                    onChange={(e) => setOwnerFormData({ ...ownerFormData, name: e.target.value })}
                    error={!ownerFormData.name.trim()}
                    helperText={!ownerFormData.name.trim() ? 'Owner name is required' : ''}
                  />
                  
                  <FormControl fullWidth>
                    <InputLabel>Nationality</InputLabel>
                    <Select
                      value={ownerFormData.nationality}
                      label="Nationality"
                      onChange={(e) => setOwnerFormData({ ...ownerFormData, nationality: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>Select nationality</em>
                      </MenuItem>
                      {countries.map((country: Country) => (
                        <MenuItem key={country.code} value={country.name}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label="Mobile Number"
                    fullWidth
                    value={ownerFormData.mobile_number}
                    onChange={(e) => setOwnerFormData({ ...ownerFormData, mobile_number: e.target.value })}
                    helperText="Optional contact number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box sx={{ 
                            fontWeight: 'bold', 
                            color: 'primary.main',
                            bgcolor: 'grey.100',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.875rem'
                          }}>
                            {getPhoneCodeByCountryName(ownerFormData.nationality || '') || '+XX'}
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <TextField
                    label="Address"
                    fullWidth
                    multiline
                    rows={3}
                    value={ownerFormData.address}
                    onChange={(e) => setOwnerFormData({ ...ownerFormData, address: e.target.value })}
                    helperText="Optional address information"
                  />
                  
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                      startIcon={<SaveIcon />}
                      variant="contained"
                      color="primary"
                      onClick={handleSaveOwner}
                    >
                      Save Owner
                    </Button>
                    <Button
                      startIcon={<CancelIcon />}
                      variant="outlined"
                      onClick={handleCancelOwnerEdit}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : property.owner ? (
                /* Owner Display */
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Name:</strong> {property.owner.name}
                  </Typography>
                  {property.owner.nationality && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Nationality:</strong> {property.owner.nationality}
                    </Typography>
                  )}
                  {property.owner.mobile_number && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Mobile:</strong> {getPhoneCodeByCountryName(property.owner.nationality || '')} {property.owner.mobile_number}
                    </Typography>
                  )}
                  {property.owner.address && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Address:</strong> {property.owner.address}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {isAdmin 
                    ? 'No owner assigned. Click "Edit Owner" to add owner information.'
                    : 'No owner assigned.'
                  }
                </Typography>
              )}
              
              {/* Property Statistics */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Property Statistics
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Property ID:</strong> {property.id}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Total Units:</strong> {units.length}
                </Typography>
                <Typography variant="body2">
                  <strong>Last Updated:</strong> {formatDate(property.updated_at, 'short')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Property Media Upload Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <PropertyMediaUpload 
            propertyId={Number(id)} 
            onMediaUpdate={() => {
              // Optionally refresh property data to get updated image URLs
              // queryClient.invalidateQueries(['property', id]);
            }}
          />
        </CardContent>
      </Card>

      {/* Facilities Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Facilities & Amenities
            </Typography>
            <Button
              startIcon={<EditIcon />}
              variant="outlined"
              size="small"
              onClick={() => setEditingFacilities(!editingFacilities)}
            >
              {editingFacilities ? 'Done' : 'Edit'}
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Available facilities and amenities at this property
          </Typography>

          {editingFacilities && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Add new facility..."
                value={newFacility}
                onChange={(e) => setNewFacility(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddFacility();
                  }
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleAddFacility}
                disabled={!newFacility.trim()}
              >
                Add
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {safeArray(property.facilities).length > 0 ? (
              safeArray(property.facilities).map((facility, index) => (
                <Chip
                  key={index}
                  label={facility}
                  size="medium"
                  variant="outlined"
                  color="secondary"
                  onDelete={editingFacilities ? () => handleRemoveFacility(index) : undefined}
                  sx={{ 
                    mb: 1,
                    fontWeight: 'medium',
                    '&:hover': {
                      backgroundColor: 'secondary.light',
                      color: 'secondary.contrastText'
                    }
                  }}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No facilities listed yet. {editingFacilities && 'Add some above!'}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Special Specifications Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Special Features & Specifications
            </Typography>
            <Button
              startIcon={<EditIcon />}
              variant="outlined"
              size="small"
              onClick={() => setEditingSpecialSpecs(!editingSpecialSpecs)}
            >
              {editingSpecialSpecs ? 'Done' : 'Edit'}
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Unique features and premium specifications of this property
          </Typography>

          {editingSpecialSpecs && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Add new special feature..."
                value={newSpecialSpec}
                onChange={(e) => setNewSpecialSpec(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddSpecialSpec();
                  }
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleAddSpecialSpec}
                disabled={!newSpecialSpec.trim()}
              >
                Add
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {safeArray(property.special_specs).length > 0 ? (
              safeArray(property.special_specs).map((spec, index) => (
                <Chip
                  key={index}
                  label={spec}
                  size="medium"
                  variant="filled"
                  color="info"
                  onDelete={editingSpecialSpecs ? () => handleRemoveSpecialSpec(index) : undefined}
                  sx={{ 
                    mb: 1,
                    fontWeight: 'medium',
                    '&:hover': {
                      backgroundColor: 'info.dark'
                    }
                  }}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No special features listed yet. {editingSpecialSpecs && 'Add some above!'}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Units Section */}
      <Card>
        <CardContent>
          <HierarchicalUnitTree
            units={units}
            onAddUnit={(parentUnit) => handleOpenUnitDialog(undefined, parentUnit)}
            onEditUnit={(unit) => handleOpenUnitDialog(unit)}
            onDeleteUnit={(unit) => handleDeleteUnit(unit.id)}
            onViewUnit={handleViewUnit}
            isAdmin={isAdmin}
          />
        </CardContent>
      </Card>

      {/* Unit Dialog */}
      <Dialog open={openUnitDialog} onClose={handleCloseUnitDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmitUnit)}>
          <DialogTitle>
            {editingUnit ? 'Edit Unit' : 'Add New Unit'}
            {!editingUnit && parentUnit && (
              <Typography variant="body2" color="text.secondary">
                Adding sub-unit to: {parentUnit.name || parentUnit.unit_number}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Parent Unit Selection */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="parent_unit_id"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      select
                      label="Parent Unit"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      error={!!errors.parent_unit_id}
                      helperText={errors.parent_unit_id?.message || 'Leave empty for root level units'}
                    >
                      <MenuItem value="">
                        <em>None (Root Level)</em>
                      </MenuItem>
                      {possibleParents.map((parent) => (
                        <MenuItem key={parent.id} value={parent.id}>
                          {'  '.repeat(parent.level || 0)} {parent.name || parent.unit_number}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              {/* Sort Order */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="sort_order"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Sort Order"
                      error={!!errors.sort_order}
                      helperText={errors.sort_order?.message || 'Controls the order of units in the tree'}
                      inputProps={{ min: 0, step: 1 }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="unit_number"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Unit Number"
                      error={!!errors.unit_number}
                      helperText={errors.unit_number?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: 'Type is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      select
                      label="Type"
                      error={!!errors.type}
                      helperText={errors.type?.message}
                    >
                      {unitTypes.map((unitType: UnitType) => (
                        <MenuItem key={unitType.id} value={unitType.name}>
                          {unitType.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Unit Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="rental_types"
                  control={control}
                  rules={{ required: 'At least one rental type is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      select
                      label="Rental Types"
                      error={!!errors.rental_types}
                      helperText={errors.rental_types?.message}
                      SelectProps={{
                        multiple: true,
                        value: field.value || [],
                        onChange: (e) => field.onChange(e.target.value),
                      }}
                    >
                      <MenuItem value="Hour">Hour</MenuItem>
                      <MenuItem value="3 Hours">3 Hours</MenuItem>
                      <MenuItem value="Daily">Daily</MenuItem>
                      <MenuItem value="Monthly">Monthly</MenuItem>
                      <MenuItem value="Yearly">Yearly</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bedrooms"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Bedrooms"
                      error={!!errors.bedrooms}
                      helperText={errors.bedrooms?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bathrooms"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Bathrooms"
                      error={!!errors.bathrooms}
                      helperText={errors.bathrooms?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="square_feet"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Square Feet"
                      error={!!errors.square_feet}
                      helperText={errors.square_feet?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="rent_amount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Rent Amount"
                      error={!!errors.rent_amount}
                      helperText={errors.rent_amount?.message}
                      InputProps={{
                        startAdornment: <AttachMoney sx={{ mr: 1 }} />,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      select
                      label="Status"
                      error={!!errors.status}
                      helperText={errors.status?.message}
                    >
                      <MenuItem value="vacant">Vacant</MenuItem>
                      <MenuItem value="occupied">Occupied</MenuItem>
                      <MenuItem value="maintenance">Under Maintenance</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={3}
                      label="Description"
                      error={!!errors.description}
                      helperText={errors.description?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUnitDialog}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createUnitMutation.isPending || updateUnitMutation.isPending}
            >
              {editingUnit ? 'Update' : 'Create'} Unit
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Unit Dialog */}
      <EditUnitDialog
        open={editUnitDialogOpen}
        onClose={() => {
          setEditUnitDialogOpen(false);
          setEditingUnit(null);
        }}
        onSubmit={handleEditUnitSubmit}
        unit={editingUnit || undefined}
        unitTypes={unitTypes}
        isLoading={updateUnitMutation.isPending}
      />
    </Container>
  );
};

export default PropertyDetailsPage;
